// Iter 637 — Cetak Massal mockup Shopee (Rido): pilih banyak pesanan → satu dokumen cetak →
//   konfirmasi Rido → seluruh batch ditandai Sudah Dicetak sekaligus (endpoint cetak yang sudah ada).
import React, { useRef, useState } from "react";
import axios from "axios";
import { Printer, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import { cetakBatchMockup, unduhBatchPdfA5L, cetakPdfA4P, A5L_PRINT_TIP, A4_PRINT_TIP } from "./cetak_mockup";
import { setPaperFormat, FORMAT_LABEL } from "./paper_format";
import { AwbSimButton, AwbSimResult, useAwbSimEnabled } from "./shopee_awb_sim";
import { MassShipPanel, useMassShipStatus } from "./shopee_mass_ship";
import "./shopee_batch_bar.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imageSnaps = (j) => (j.warehouse_sent_snapshot || []).filter(s => s.file_id && (!s.content_type || String(s.content_type).startsWith("image")));
// Iter 643 — Hanya file yang sudah DIPASTIKAN A5 Landscape ikut cetak massal A5. A4 & belum dipastikan dilewati.
const a5Snaps = (j) => imageSnaps(j).filter(sn => sn.paper_format === "A5L");
const unknownSnaps = (j) => imageSnaps(j).filter(sn => !sn.paper_format);
const a4Snaps = (j) => imageSnaps(j).filter(sn => sn.paper_format === "A4P" || sn.paper_format === "A4L");
const countA4 = (list) => list.reduce((n, j) => n + a4Snaps(j).length, 0);
const countUnknown = (list) => list.reduce((n, j) => n + unknownSnaps(j).length, 0);
const formatReady = (j) => unknownSnaps(j).length === 0;
const allA5 = (j) => imageSnaps(j).length > 0 && imageSnaps(j).every(sn => sn.paper_format === "A5L");

// Penanda ukuran kertas dari format tersimpan per file (tanpa tebakan): A5 / A4 / A4 + A5 / Belum Pilih Format.
export const paperLabel = (j) => {
  const snaps = imageSnaps(j);
  if (!snaps.length || unknownSnaps(j).length) return null;
  const a5 = snaps.some(sn => sn.paper_format === "A5L"), a4 = snaps.some(sn => sn.paper_format === "A4P" || sn.paper_format === "A4L");
  return a4 && a5 ? "A4 + A5" : a4 ? "A4" : a5 ? "A5" : null;
};
export function PaperBadge({ job }) {
  const t = paperLabel(job);
  return <span className={`shopee-paper-badge${t ? ` is-${t === "A5" ? "a5" : "a4"}` : " is-none"}`} data-testid={`wh-shopee-paper-badge-${job.id}`}
    title={t ? `Ukuran kertas: ${t}` : "Format kertas belum dipilih operator Mockup"}>{t || "Belum Pilih Format"}</span>;
}

export const shopeeCancelled = (j) => !!(j.shopee_cancelled || j.shopee_in_cancel);
// Batal FINAL (resmi Shopee / internal final) → bukan pekerjaan aktif. Pengajuan batal & HOLD tetap sementara.
export const finalCancelled = (j) => !!j && (j.cancellation_status === "canceled_internal" || !!j.shopee_cancelled);
export const isHeld = (j) => !!(j?.hold && j.hold.active);
export const batchEligible = (j, mode) => j.warehouse_state === "sent" && j.status === "FIX" && !shopeeCancelled(j) && !finalCancelled(j) && !isHeld(j)
  && !(j.pending_request && j.pending_request.status === "pending") && imageSnaps(j).length > 0
  && (mode === "sudah" ? !!j.mockup_printed_at : !j.mockup_printed_at);

export function BatchSelectBox({ job, mode, selected, onToggle }) {
  if (shopeeCancelled(job) && job.warehouse_state === "sent") return <label className="shopee-batch-pick" title="DIBATALKAN SHOPEE — JANGAN PRODUKSI"
    data-testid={`wh-shopee-batch-pick-${job.id}`}><input type="checkbox" disabled checked={false} onChange={() => {}}
      aria-label={`${job.order_number} dibatalkan Shopee`} data-testid={`wh-shopee-batch-check-${job.id}`}/></label>;
  if (isHeld(job) && job.warehouse_state === "sent") return <label className="shopee-batch-pick" title="HOLD — tidak dapat dipilih untuk cetak sampai HOLD dilepas Tim Mockup"
    data-testid={`wh-shopee-batch-pick-${job.id}`}><input type="checkbox" disabled checked={false} onChange={() => {}}
      aria-label={`${job.order_number} sedang HOLD`} data-testid={`wh-shopee-batch-check-${job.id}`}/></label>;
  if (!batchEligible(job, mode)) return null;
  if (!formatReady(job)) return <label className="shopee-batch-pick" title="Belum siap cetak — format kertas belum dipastikan"
    data-testid={`wh-shopee-batch-pick-${job.id}`}><input type="checkbox" disabled checked={false} onChange={() => {}}
      aria-label={`${job.order_number} belum siap cetak`} data-testid={`wh-shopee-batch-check-${job.id}`}/>
    <span className="shopee-batch-notready" data-testid={`wh-shopee-batch-notready-${job.id}`}>Belum siap: format kertas belum dipastikan</span></label>;
  return <label className="shopee-batch-pick" title="Pilih untuk cetak massal (Shift+klik = pilih rentang)" data-testid={`wh-shopee-batch-pick-${job.id}`}>
    <input type="checkbox" aria-label={`Pilih ${job.order_number} untuk cetak massal`} checked={selected.has(job.id)}
      onClick={(e) => onToggle(job.id, e)} onChange={() => {}} data-testid={`wh-shopee-batch-check-${job.id}`}/>
  </label>;
}

async function pool(list, n, fn) {
  let i = 0;
  const out = [];
  await Promise.all(Array.from({ length: Math.min(n, list.length) }, async () => {
    while (i < list.length) { const k = i++; out[k] = await fn(list[k]); }
  }));
  return out;
}

export function ShopeeBatchPrintBar({ jobs, mode, selected, setSelected, ensureProcessing, onDone }) {
  const allEligible = jobs.filter(j => batchEligible(j, mode));
  const eligible = allEligible.filter(formatReady);
  const needFormat = allEligible.filter(j => !formatReady(j));
  const ready = eligible.filter(allA5);
  const chosen = eligible.filter(j => selected.has(j.id));
  const [stage, setStage] = useState(null); // null | paper | preparing | confirm | marking
  const [prog, setProg] = useState({ done: 0, total: 0 });
  const [printed, setPrinted] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [a4Held, setA4Held] = useState([]);
  const busy = useRef(false);
  const sess = useRef({ batch: [], done: new Set(), failed: [], paper: "A5L" });
  const simOn = useAwbSimEnabled();
  const [simRes, setSimRes] = useState(null);
  const [shipJobs, setShipJobs] = useState(null);
  const shipSt = useMassShipStatus();
  if (allEligible.length === 0 && !stage && !simRes && !shipJobs) return null;
  const allShown = ready.length > 0 && ready.every(j => selected.has(j.id));
  const chosenA4 = countA4(chosen);
  const shipMax = shipSt?.max_per_run || 1;

  // Iter 640 — kumulatif per sesi: file yang sudah ikut tercetak tidak dicetak lagi saat "Cetak Ulang yang Gagal".
  const startPrint = async (paper, retryOnly = false, asPdf = false) => {
    if (busy.current) return;
    busy.current = true;
    const S = sess.current;
    if (!retryOnly) {
      S.batch = chosen.slice(); S.done = new Set(); S.failed = [];
      // status BATAL resmi Shopee terkini — pesanan batal / tak terverifikasi dikeluarkan dari batch
      const real = S.batch.filter(j => !j.is_simulation).map(j => j.id);
      if (real.length) {
        let chk;
        try { chk = (await axios.post(`${API}/shopee/mass-ship/print-check`, { job_ids: real }, { withCredentials: true })).data; }
        catch (e) { busy.current = false; toast.error(e?.response?.data?.detail || "Status Shopee belum dapat diverifikasi — periksa ulang sebelum cetak."); return; }
        const bad = chk.cancelled || [];   // hanya BATAL resmi dikeluarkan; belum terverifikasi = peringatan, tetap dicetak
        if ((chk.unverified || []).length) toast.warning(`Status Shopee belum dapat diverifikasi (tetap dicetak): ${chk.unverified.map(x => x.order_number).join(", ")}`);
        const badIds = new Set(bad.map(x => x.job_id));
        S.batch = S.batch.filter(j => !badIds.has(j.id));
        setExcluded(bad);
        if (bad.length) setSelected(prev => { const n = new Set(prev); badIds.forEach(id => n.delete(id)); return n; });
        if (!S.batch.length) { busy.current = false; toast.error("Tidak ada pesanan yang boleh dicetak."); return; }
      }
    }
    S.paper = paper; S.pdf = asPdf;
    const a4 = paper === "A4R";
    const all = S.batch.flatMap(j => (a4 ? a4Snaps(j) : a5Snaps(j)).map(sn => ({ orderNumber: j.order_number, fileId: sn.file_id, jobId: j.id,
      paperFormat: sn.paper_format, label: `${j.order_number} · ${sn.title || sn.filename || ""}` })));
    const todo = all.filter(it => !S.done.has(it.fileId)).map(it => ({ ...it, onLoaded: () => setProg(p => ({ ...p, done: p.done + 1 })) }));
    setProg({ done: 0, total: todo.length });
    setStage("preparing");
    let r = null;
    try {
      r = await (a4 ? cetakPdfA4P(todo) : asPdf ? unduhBatchPdfA5L(todo) : cetakBatchMockup(todo, paper));
      (r.printedFileIds || []).forEach(id => S.done.add(id));
    } catch (e) {
      if (!e?.missing) toast.error(`${e?.message || "Gagal menyiapkan dokumen cetak."} Pesanan tetap Belum Dicetak.`);
    } finally { busy.current = false; }
    const full = S.batch.filter(j => imageSnaps(j).every(sn => S.done.has(sn.file_id)));
    S.failed = all.filter(it => !S.done.has(it.fileId));
    const a4n = S.batch.filter(j => (a4 ? a4Snaps(j) : a5Snaps(j)).length < imageSnaps(j).length).length;
    setPrinted(full);
    const failedJobs = new Set(S.failed.map(it => it.jobId));
    setSkipped(S.batch.filter(j => failedJobs.has(j.id)));
    setA4Held(S.batch.filter(j => !full.includes(j) && !failedJobs.has(j.id)));
    setStage(full.length || S.failed.length || a4n ? "confirm" : null);
  };

  const confirmPrinted = async () => {
    if (busy.current) return;
    busy.current = true;
    setStage("marking");
    const reprint = mode === "sudah";
    const res = await pool(printed, 3, async (j) => {
      try {
        if (reprint) await axios.post(`${API}/shopee/${j.id}/mark-mockup-reprinted`, {}, { withCredentials: true });
        else {
          await ensureProcessing(j);
          await axios.post(`${API}/shopee/${j.id}/mark-mockup-printed`, { file_ids: imageSnaps(j).map(sn => sn.file_id) }, { withCredentials: true });
        }
        return { j, ok: true };
      } catch (e) { return { j, ok: false, msg: e?.response?.data?.detail || e.message }; }
    });
    const bad = res.filter(x => !x.ok);
    setSelected(new Set([...bad.map(x => x.j.id), ...skipped.map(j => j.id), ...a4Held.map(j => j.id)])); // yang gagal tetap terpilih untuk dicoba lagi
    setStage(null);
    busy.current = false;
    await onDone();
    if (bad.length === 0) toast.success(reprint ? `${res.length} cetak ulang dicatat — status tetap Sudah Dicetak.` : `${res.length} pesanan ditandai Sudah Dicetak.`);
    else toast.error(`${res.length - bad.length} berhasil, ${bad.length} gagal dicatat${reprint ? "" : " (tetap Belum Dicetak)"}: ${bad.map(x => `${x.j.order_number} — ${x.msg}`).join("; ")}`);
  };

  return <div className="shopee-batch-bar" data-testid="wh-shopee-batch-bar">
    <label className="shopee-batch-all" data-testid="wh-shopee-batch-select-all-wrap">
      <input type="checkbox" checked={allShown} disabled={ready.length === 0} ref={el => { if (el) el.indeterminate = chosen.length > 0 && !allShown; }}
        onChange={() => setSelected(allShown ? new Set() : new Set(ready.map(j => j.id)))} data-testid="wh-shopee-batch-select-all"/>
      {allShown ? "Batal Pilih Semua" : `Pilih Semua yang Siap (${ready.length})`}
    </label>
    <span className="shopee-batch-sep" aria-hidden="true">·</span>
    <span className="shopee-batch-count" data-testid="wh-shopee-batch-count"><b>{chosen.length}</b> mockup dipilih · <b>{ready.length}</b> siap dicetak</span>
    {excluded.length > 0 && <span className="small" style={{ color: "#b3261e", fontWeight: 600 }} data-testid="wh-shopee-batch-excluded">
      {excluded.length} dilewati (tidak dicetak): {excluded.map(x => `${x.order_number} — ${x.reason}`).join("; ")}
      <button type="button" className="link-btn small" onClick={() => setExcluded([])} data-testid="wh-shopee-batch-excluded-close">Tutup</button></span>}
    <span className="shopee-batch-break" aria-hidden="true"/>
    <div className="shopee-batch-actions">
      <button type="button" className="primary small-btn" disabled={chosen.length === 0 || !!stage} onClick={() => startPrint("A5L")}
        data-testid="wh-shopee-batch-print" aria-label={mode === "sudah" ? `Cetak Ulang (${chosen.length})` : `Cetak Mockup (${chosen.length})`}>
        <Printer size={17} aria-hidden="true"/>{mode === "sudah" ? "Cetak Ulang" : "Cetak Mockup"} ({chosen.length})</button>
      <button type="button" className="soft-btn small-btn" disabled={chosen.length === 0 || !!stage} onClick={() => setStage("paper")}
        data-testid="wh-shopee-batch-more-open"><MoreVertical size={16} aria-hidden="true"/>Opsi lain</button>
    </div>
    {chosenA4 > 0 && !stage && <span className="small shopee-batch-over" data-testid="wh-shopee-batch-a4-hint">{chosenA4} file A4 dicetak lewat Opsi lain</span>}
    {needFormat.length > 0 && !stage && <button type="button" className="soft-btn small-btn" onClick={() => setStage("format")}
      data-testid="wh-shopee-batch-format-open">Pastikan Format ({countUnknown(needFormat)})</button>}
    {stage === "format" && <FormatConfirmModal jobs={needFormat} onClose={() => setStage(null)} onDone={onDone}/>}
    {stage === "preparing" && <span className="muted small" data-testid="wh-shopee-batch-preparing">Menyiapkan dokumen cetak… {prog.done}/{prog.total} mockup</span>}
    {stage === "marking" && <span className="muted small" data-testid="wh-shopee-batch-marking">Menandai Sudah Dicetak…</span>}
    {stage === "paper" && <PaperChooser chosen={chosen} chosenA4={chosenA4} shipSt={shipSt} simOn={simOn} shipMax={shipMax}
      onClose={() => setStage(null)} onPrint={startPrint}
      onShip={(st) => { setShipJobs({ list: chosen.slice(), step: st }); setStage(null); }}
      onSim={(r) => { setStage(null); setSimRes(r); }}/>}
    {simRes && <AwbSimResult res={simRes} onClose={() => setSimRes(null)}/>}
    {shipJobs && <MassShipPanel key={shipJobs.list.map(j => j.id).join(",")} jobs={shipJobs.list.map(s => jobs.find(j => j.id === s.id) || s)} initialStep={shipJobs.step} status={shipSt} ensureProcessing={ensureProcessing} onDone={onDone} onClose={() => setShipJobs(null)}/>}
    {stage === "confirm" && <div className="modal-backdrop" style={{ zIndex: 1000 }}>
      <div className="modal" style={{ maxWidth: 480 }} data-testid="wh-shopee-batch-confirm">
        <h3 style={{ marginTop: 0 }}>{mode === "sudah" ? `Apakah cetak ulang ${printed.length} pesanan berhasil?` : `Apakah ${printed.length} pesanan berhasil dicetak?`}</h3>
        <div style={{ fontWeight: 600, marginBottom: 6 }} data-testid="wh-shopee-batch-summary">{printed.length} tercetak · {skipped.length} gagal memuat gambar</div>
        <p className="muted small">{mode === "sudah" ? "Tekan \"Ya\" untuk mencatat cetak ulang. Status tetap Sudah Dicetak." : "Periksa hasil printer. Status baru berubah setelah Anda menekan \"Ya\"."}</p>
        <ol className="small" style={{ maxHeight: 180, overflow: "auto", margin: "0 0 0 18px", padding: 0 }} data-testid="wh-shopee-batch-confirm-list">
          {printed.map(j => <li key={j.id}>{j.order_number}{j.customer_account ? ` · ${j.customer_account}` : ""}</li>)}</ol>
        {a4Held.length > 0 && <div className="warn-box small" data-testid="wh-shopee-batch-a4-held">Punya file format lain / Format Belum Dipastikan (tidak ikut cetak ini, tetap Belum Dicetak): {a4Held.map(j => j.order_number).join(", ")}</div>}
        {skipped.length > 0 && <div className="warn-box small" data-testid="wh-shopee-batch-skipped">Gagal memuat gambar (sudah dicoba ulang otomatis{mode === "sudah" ? "" : ", tetap Belum Dicetak"}): {skipped.map(j => j.order_number).join(", ")}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <button type="button" className="primary" disabled={printed.length === 0} onClick={confirmPrinted} data-testid="wh-shopee-batch-confirm-yes">Ya, semua tercetak ({printed.length})</button>
          {skipped.length > 0 && <button type="button" className="soft-btn" onClick={() => startPrint(sess.current.paper, true, sess.current.pdf)} data-testid="wh-shopee-batch-retry-failed">Cetak Ulang yang Gagal ({skipped.length})</button>}
          <button type="button" className="soft-btn" onClick={() => setStage(null)} data-testid="wh-shopee-batch-confirm-no">{mode === "sudah" ? "Belum / Gagal — tidak dicatat" : "Belum / Gagal — tetap Belum Dicetak"}</button>
        </div>
      </div>
    </div>}
  </div>;
}

function useResiReady(chosen, on) {
  const [ready, setReady] = useState(null);
  const ids = chosen.map(j => j.id).join(",");
  React.useEffect(() => {
    if (!on || !ids) return undefined;
    let live = true;
    axios.post(`${API}/shopee/mass-ship/state`, { job_ids: ids.split(",") }, { withCredentials: true })
      .then(r => { if (live) setReady((r.data.items || []).filter(a => a.tracking_state === "available").length); })
      .catch(() => {});
    return () => { live = false; };
  }, [ids, on]);
  return ready;
}

// Pilih cara cetak: ringkas (jumlah + 2 tombol utama); petunjuk kertas di Bantuan, unduh PDF di Opsi tambahan.
function PaperChooser({ chosen, chosenA4, shipSt, simOn, shipMax, onClose, onPrint, onShip, onSim }) {
  const ready = useResiReady(chosen, !!shipSt?.switch_on);
  const waiting = ready == null ? null : chosen.length - ready;
  const locked = !shipSt?.can_open;
  return <div className="modal-backdrop" style={{ zIndex: 1000 }} onClick={onClose}>
    <div className="modal bpx" onClick={e => e.stopPropagation()} data-testid="wh-shopee-batch-paper">
      <h3 style={{ marginTop: 0 }}>Opsi lain · {chosen.length} Pesanan</h3>
      <div className="msx-chips">
        <span className="msx-chip" data-testid="wh-shopee-batch-count-selected"><b>{chosen.length}</b> Dipilih</span>
        <span className="msx-chip is-ok" data-testid="wh-shopee-batch-count-ready"><b>{ready ?? "—"}</b> Resi siap</span>
        <span className={`msx-chip${waiting ? " is-wait" : ""}`} data-testid="wh-shopee-batch-count-waiting"><b>{waiting ?? "—"}</b> Menunggu</span>
      </div>
      <button type="button" className="primary bpx-btn" disabled={locked || chosen.length === 0} onClick={() => onShip(ready ? 2 : 0)}
        data-testid="wh-shopee-mass-ship-print">Cetak Mockup + Resi{ready != null ? ` (${ready})` : ""}{locked ? " — Belum Diaktifkan" : ""}</button>
      {shipSt && shipSt.can_open && <button type="button" className="soft-btn bpx-btn" disabled={chosen.length > shipMax} onClick={() => onShip(0)}
        data-testid="wh-shopee-ship-open-direct">Atur Pengiriman ({chosen.length} Pesanan){chosen.length > shipMax ? ` — maks ${shipMax}` : ""}</button>}
      <button type="button" className="soft-btn bpx-btn" onClick={() => onPrint("A5L")} data-testid="wh-shopee-batch-paper-a5l">Cetak Mockup Saja ({chosen.length})</button>
      {waiting > 0 && <p className="muted small" data-testid="wh-shopee-batch-waiting-note">{waiting} resi belum tersedia dan bisa dicoba lagi nanti.</p>}
      {shipSt && !shipSt.write_enabled && <p className="muted small" data-testid="wh-shopee-mass-ship-locked">Resi: {shipSt.killed && shipSt.switch_on ? "dimatikan sementara oleh Owner" : "menunggu aktivasi Owner"}</p>}
      {shipSt?.pilot && shipSt.write_enabled && <p className="muted small" data-testid="wh-shopee-mass-ship-pilot">Uji coba: hanya pesanan {shipSt.pilot_orders?.length ? shipSt.pilot_orders.join(", ") : "(menunggu nomor dari Owner)"} · maks {shipSt.max_per_run} per proses.</p>}
      {chosenA4 > 0 && <div className="warn-box small" data-testid="wh-shopee-batch-a4-note">{chosenA4} file A4 tidak ikut cetak A5 — pakai "Cetak Mockup A4" di Opsi tambahan.</div>}
            <details className="small bpx-more" data-testid="wh-shopee-batch-more">
        <summary>Opsi tambahan</summary>
        <div className="bpx-row">
          <button type="button" className="soft-btn" onClick={() => onPrint("A5L", false, true)} data-testid="wh-shopee-batch-paper-pdf">Unduh PDF Mockup Saja (A5 Landscape)</button>
          {chosenA4 > 0 && <button type="button" className="soft-btn" onClick={() => onPrint("A4R", false, true)} data-testid="wh-shopee-batch-paper-a4">Cetak Mockup A4 (PDF A4 Portrait) — {chosenA4} file</button>}
          {simOn && <span data-testid="wh-shopee-awb-sim-section"><AwbSimButton jobs={chosen} onOpen={onSim}/></span>}
        </div>
      </details>
      <details className="small bpx-more" data-testid="wh-shopee-batch-help">
        <summary>Bantuan</summary>
        <p><b>Cetak Mockup Saja:</b> kertas A5 Landscape (210 × 148 mm, mendatar) · tanpa resi · 1 file mockup = 1 halaman · hanya file A5.</p>
        <p className="muted" data-testid="wh-shopee-batch-a5l-tip">{A5L_PRINT_TIP}</p>
        <p><b>Cetak Mockup + Resi:</b> kertas A5 Portrait (148 × 210 mm, tegak) · mockup otomatis diputar 90° · resi tetap tegak · urutan Mockup → Resi per pesanan.</p>
        <p className="muted" data-testid="wh-shopee-batch-a4-tip">Mockup A4: PDF A4 Portrait (210 × 297 mm), 1 file = 1 halaman. A4 Landscape otomatis diputar 90°. {A4_PRINT_TIP}</p>
      </details>
      <button type="button" className="link-btn small" onClick={onClose} data-testid="wh-shopee-batch-paper-cancel">Tutup</button>
    </div>
  </div>;
}

// Iter 643 — Pastikan Format Massal: Rido memeriksa thumbnail lalu menetapkan beberapa file sekaligus.
//   Hanya metadata format; status cetak & workflow tidak berubah.
function FormatConfirmModal({ jobs, onClose, onDone }) {
  const files = jobs.flatMap(j => unknownSnaps(j).map(sn => ({ ...sn, order: j.order_number })));
  const [pick, setPick] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setPick(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const apply = async (fmt) => {
    setBusy(true);
    try {
      await setPaperFormat([...pick], fmt);
      toast.success(`${pick.size} file ditetapkan ${FORMAT_LABEL[fmt]}. Status cetak tidak berubah.`);
      await onDone();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Gagal menyimpan format."); }
    finally { setBusy(false); }
  };
  return <div className="modal-backdrop" style={{ zIndex: 1000 }} onClick={() => !busy && onClose()}>
    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }} data-testid="wh-shopee-format-modal">
      <h3 style={{ marginTop: 0 }}>Pastikan Format ({files.length} file belum dipastikan)</h3>
      <p className="muted small">Periksa gambar, centang file yang formatnya sama, lalu tetapkan sekaligus. Desain asli dan status cetak tidak diubah.</p>
      <button type="button" className="soft-btn small-btn" onClick={() => setPick(pick.size === files.length ? new Set() : new Set(files.map(f => f.file_id)))}
        data-testid="wh-shopee-format-select-all">{pick.size === files.length ? "Batal Pilih Semua" : "Pilih Semua"}</button>
      <div className="pf-grid" data-testid="wh-shopee-format-list">
        {files.map(f => <label key={f.file_id} className={`pf-item${pick.has(f.file_id) ? " active" : ""}`} data-testid={`wh-shopee-format-item-${f.file_id}`}>
          <input type="checkbox" checked={pick.has(f.file_id)} onChange={() => toggle(f.file_id)} data-testid={`wh-shopee-format-check-${f.file_id}`}/>
          <img src={`${API}/shopee/files/${f.file_id}/thumbnail?size=300`} alt={f.title || f.filename} loading="lazy"/>
          <span className="small">{f.order}<br/><span className="muted">{f.title || f.filename}</span></span>
        </label>)}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        <button type="button" className="primary" disabled={busy || !pick.size} onClick={() => apply("A5L")} data-testid="wh-shopee-format-set-a5l">Tetapkan A5 Landscape ({pick.size})</button>
        <button type="button" className="soft-btn" disabled={busy || !pick.size} onClick={() => apply("A4P")} data-testid="wh-shopee-format-set-a4p">Tetapkan A4 Portrait ({pick.size})</button>
        <button type="button" className="soft-btn" disabled={busy || !pick.size} onClick={() => apply("A4L")} data-testid="wh-shopee-format-set-a4l">Tetapkan A4 Landscape ({pick.size})</button>
        <button type="button" className="soft-btn" disabled={busy} onClick={onClose} data-testid="wh-shopee-format-cancel">Tutup</button>
      </div>
    </div>
  </div>;
}
